import type {
  ForecastRequest,
  ForecastResponse,
  RerunRequest,
  ForecastQuestion,
  ForecastRun,
  Project,
  ProjectDetail,
  UserPrediction,
  UserPredictionRequest,
  ChatRequest,
  StatusCheckRequest,
  StatusCheckResponse,
  SavedStatusCheck,
  SavedStatusCheckListResponse,
  MarketCheckResponse,
  ScheduledRerun,
  ScheduledRerunRequest,
  ScheduledRerunResponse,
  GenerateScenariosResponse,
  SavedScenarios,
  SavedScenariosListResponse,
  Scenario,
} from '../types'

const API_BASE = '/api'

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// Forecast endpoints
export async function startForecast(request: ForecastRequest): Promise<ForecastResponse> {
  return fetchJSON<ForecastResponse>(`${API_BASE}/forecast`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function rerunForecast(questionId: string, request: RerunRequest): Promise<ForecastResponse> {
  return fetchJSON<ForecastResponse>(`${API_BASE}/questions/${questionId}/rerun`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function continueForecast(jobId: string): Promise<void> {
  await fetch(`${API_BASE}/forecast/${jobId}/continue`, {
    method: 'POST',
  })
}

export async function getForecast(jobId: string): Promise<ForecastRun> {
  return fetchJSON<ForecastRun>(`${API_BASE}/forecast/${jobId}`)
}

export async function deleteForecast(jobId: string): Promise<void> {
  await fetch(`${API_BASE}/forecast/${jobId}`, {
    method: 'DELETE',
  })
}

export async function getRecentForecasts(limit: number = 20): Promise<ForecastRun[]> {
  return fetchJSON<ForecastRun[]>(`${API_BASE}/forecasts?limit=${limit}`)
}

export async function getAllForecasts(): Promise<ForecastRun[]> {
  return fetchJSON<ForecastRun[]>(`${API_BASE}/admin/forecasts`)
}

// Question endpoints
export async function getQuestions(): Promise<ForecastQuestion[]> {
  return fetchJSON<ForecastQuestion[]>(`${API_BASE}/questions`)
}

export async function getQuestion(questionId: string): Promise<ForecastQuestion> {
  return fetchJSON<ForecastQuestion>(`${API_BASE}/questions/${questionId}`)
}

export async function statusCheckQuestion(questionId: string, request: StatusCheckRequest = {}): Promise<StatusCheckResponse> {
  return fetchJSON<StatusCheckResponse>(`${API_BASE}/questions/${questionId}/status-check`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function getSavedStatusChecks(questionId: string, limit: number = 10): Promise<SavedStatusCheckListResponse> {
  return fetchJSON<SavedStatusCheckListResponse>(`${API_BASE}/questions/${questionId}/status-checks?limit=${limit}&mode=news`)
}

export async function saveNewsSummary(questionId: string, payload: StatusCheckResponse): Promise<SavedStatusCheck> {
  return fetchJSON<SavedStatusCheck>(`${API_BASE}/questions/${questionId}/status-check/save`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function marketCheckQuestion(questionId: string): Promise<MarketCheckResponse> {
  return fetchJSON<MarketCheckResponse>(`${API_BASE}/questions/${questionId}/market-check`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function getSavedMarketChecks(questionId: string, limit: number = 10): Promise<SavedStatusCheckListResponse> {
  return fetchJSON<SavedStatusCheckListResponse>(`${API_BASE}/questions/${questionId}/market-checks?limit=${limit}`)
}

export async function saveMarketSnapshot(questionId: string, payload: MarketCheckResponse): Promise<SavedStatusCheck> {
  return fetchJSON<SavedStatusCheck>(`${API_BASE}/questions/${questionId}/market-check/save`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Project endpoints
export async function getProjects(): Promise<Project[]> {
  return fetchJSON<Project[]>(`${API_BASE}/projects`)
}

export async function getProject(projectId: string): Promise<ProjectDetail> {
  return fetchJSON<ProjectDetail>(`${API_BASE}/projects/${projectId}`)
}

export async function createProject(name: string, description?: string): Promise<Project> {
  return fetchJSON<Project>(`${API_BASE}/projects`, {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  })
}

export async function updateProject(projectId: string, name: string, description?: string): Promise<Project> {
  return fetchJSON<Project>(`${API_BASE}/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify({ name, description }),
  })
}

export async function deleteProject(projectId: string): Promise<void> {
  await fetch(`${API_BASE}/projects/${projectId}`, {
    method: 'DELETE',
  })
}

export async function assignQuestionToProject(questionId: string, projectId: string): Promise<void> {
  await fetchJSON(`${API_BASE}/questions/${questionId}/project`, {
    method: 'PUT',
    body: JSON.stringify({ project_id: projectId }),
  })
}

// User prediction endpoints
export async function saveUserPrediction(prediction: UserPredictionRequest): Promise<UserPrediction> {
  return fetchJSON<UserPrediction>(`${API_BASE}/user-predictions`, {
    method: 'POST',
    body: JSON.stringify(prediction),
  })
}

export async function getUserPredictions(questionId: string): Promise<UserPrediction[]> {
  return fetchJSON<UserPrediction[]>(`${API_BASE}/questions/${questionId}/user-predictions`)
}

export async function deleteUserPrediction(predictionId: string): Promise<void> {
  await fetch(`${API_BASE}/user-predictions/${predictionId}`, {
    method: 'DELETE',
  })
}

// Chat endpoint (returns a stream, handled separately)
export async function chatStream(request: ChatRequest): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return response.body!
}

// Models endpoint
export async function getModels() {
  return fetchJSON(`${API_BASE}/models`)
}

// Scheduled Reruns endpoints
export async function createScheduledRerun(request: ScheduledRerunRequest): Promise<ScheduledRerunResponse> {
  return fetchJSON<ScheduledRerunResponse>(`${API_BASE}/admin/schedules`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function getSchedulesForQuestion(questionId: string): Promise<ScheduledRerun[]> {
  return fetchJSON<ScheduledRerun[]>(`${API_BASE}/questions/${questionId}/schedules`)
}

export async function getAllSchedules(): Promise<ScheduledRerun[]> {
  return fetchJSON<ScheduledRerun[]>(`${API_BASE}/admin/schedules`)
}

export async function toggleSchedule(scheduleId: string, isActive: boolean): Promise<void> {
  await fetchJSON(`${API_BASE}/admin/schedules/${scheduleId}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  })
}

export async function deleteSchedule(scheduleId: string): Promise<void> {
  await fetchJSON(`${API_BASE}/admin/schedules/${scheduleId}`, {
    method: 'DELETE',
  })
}

// Scenarios endpoints
export async function generateScenarios(questionId: string): Promise<GenerateScenariosResponse> {
  return fetchJSON<GenerateScenariosResponse>(`${API_BASE}/questions/${questionId}/generate-scenarios`, {
    method: 'POST',
  })
}

export async function saveScenarios(
  questionId: string,
  scenarios: Scenario[],
  probability: number
): Promise<SavedScenarios> {
  return fetchJSON<SavedScenarios>(`${API_BASE}/questions/${questionId}/scenarios/save`, {
    method: 'POST',
    body: JSON.stringify({ scenarios, probability }),
  })
}

export async function getSavedScenarios(questionId: string, limit: number = 10): Promise<SavedScenariosListResponse> {
  return fetchJSON<SavedScenariosListResponse>(`${API_BASE}/questions/${questionId}/scenarios?limit=${limit}`)
}

